import { useEffect, useState } from "react";
import { Table, Stack, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import { getRouletteScripts, checkDownloadLimit, downloadFile } from "../utils/Database";
import { showToast } from "../utils/Toasts";
import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";

import useUserStore from "../stores/User";

const COUNT_OPTIONS = [5, 10, 20, 50];

type RouletteScript = {
    file_id: string;
    "Script Title": string;
    "Writer(s)": string;
    "Draft Date": string | null;
    additional_info_concat: string | null;
    "Date Added": string;
    storage_hash: string;
    google_drive_hash: string | null;
};

export default function ScriptRoulette() {
    const navigate = useNavigate();
    const isSmall = useMediaQuery({ query: "(max-width: 576px)" });

    const userData = useUserStore((state) => state.data);
    const unlimitedDownloads = userData?.isApprover || false;

    const [scripts, setScripts] = useState<RouletteScript[]>([]);
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [spinning, setSpinning] = useState(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    async function spin(c = count) {
        setSpinning(true);
        const data = await getRouletteScripts(c);
        setScripts(data);
        setSpinning(false);
        setLoading(false);
    }

    useEffect(() => {
        setLoading(true);
        spin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleDownload(script: RouletteScript) {
        if (downloadingId !== null) return;
        setDownloadingId(script.file_id);

        let remaining = 0;
        let downloadLimit = 0;

        if (!unlimitedDownloads) {
            const allowance = await checkDownloadLimit(userData?.user_id);
            if (!allowance) {
                setDownloadingId(null);
                return;
            }
            remaining = allowance.remaining;
            downloadLimit = allowance.limit;
        }

        if (remaining > 0 || unlimitedDownloads) {
            const success = await downloadFile(script.file_id, userData?.user_id);
            setDownloadingId(null);
            if (success && !unlimitedDownloads) {
                showToast("info", `You have ${remaining - 1 === 0 ? "no more" : remaining - 1} download(s) remaining today.`);
            }
        } else {
            showToast("info", `Sorry, you've reached the daily download limit of ${downloadLimit}.`);
            setDownloadingId(null);
        }
    }

    return (
        <div className="hive-container d-flex">
            <main>
                <div className="px-3 px-md-5 pb-2">
                    <Stack direction={isSmall ? "vertical" : "horizontal"} gap={3} className="align-items-start align-items-sm-center mb-3">
                        <div>
                            <h2 className="mb-0"><img src="/roulette-wheel.png" alt="Roulette" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 10, verticalAlign: 'middle' }} />Script Roulette</h2>
                            <p className="text-muted mb-0 small">A random selection of popular scripts — spin to discover something new.</p>
                        </div>
                        <Stack direction="horizontal" gap={2} className="ms-sm-auto flex-shrink-0">
                            <Form.Select
                                size="sm"
                                value={count}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setCount(val);
                                    spin(val);
                                }}
                                disabled={spinning}
                                style={{ width: "auto" }}
                            >
                                {COUNT_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n} scripts
                                    </option>
                                ))}
                            </Form.Select>
                            <button
                                className="btn btn-primary navbutton"
                                onClick={() => spin()}
                                disabled={spinning}
                            >
                                {spinning ? <LoadingText text="Spinning..." className="mb-0" /> : "Spin Again"}
                            </button>
                        </Stack>
                    </Stack>
                </div>

                {loading ? (
                    <LoadingRing />
                ) : spinning ? (
                    <div className="mx-3 mx-md-5 mt-3">
                        <LoadingRing />
                    </div>
                ) : scripts.length === 0 ? (
                    <div className="mx-3 mx-md-5 mt-3">No scripts found.</div>
                ) : (
                    <div className="mt-3 mb-4 mx-3 mx-md-5">
                        {isSmall ? (
                            <Stack gap={2}>
                                {scripts.map((script) => (
                                    <div key={script.file_id} className="d-flex align-items-start gap-2 py-2 border-bottom">
                                        <button
                                            className="icon-only-button flex-shrink-0"
                                            style={{ cursor: downloadingId === script.file_id ? "not-allowed" : "pointer" }}
                                            disabled={downloadingId === script.file_id}
                                            onClick={() => handleDownload(script)}
                                        >
                                            {downloadingId === script.file_id ? (
                                                <LoadingText text="" className="mb-0" />
                                            ) : (
                                                <i className="bi bi-file-earmark-arrow-down-fill" aria-label="Download" title="Download"></i>
                                            )}
                                        </button>
                                        <div className="min-width-0">
                                            <a
                                                className="d-block text-break small fw-semibold"
                                                href={`/database/script/${script.file_id}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/database/script/${script.file_id}`, { state: { from: "roulette" } });
                                                }}
                                            >
                                                {script["Script Title"]}
                                            </a>
                                            <span className="text-muted small">{script["Writer(s)"]}</span>
                                        </div>
                                    </div>
                                ))}
                            </Stack>
                        ) : (
                            <div className="script-table table-container">
                                <div className="table-responsive">
                                    <Table striped hover size="sm">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 30 }} className="text-center">
                                                    <i className="bi bi-file-earmark-arrow-down" aria-label="Download" title="Download"></i>
                                                </th>
                                                <th style={{ minWidth: 400 }}>Script Title</th>
                                                <th style={{ minWidth: 300 }}>Writer(s)</th>
                                                <th style={{ width: 150 }}>Draft Date</th>
                                                <th style={{ minWidth: 200 }}>Additional Info</th>
                                                <th style={{ width: 115 }}>Date Added</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scripts.map((script) => (
                                                <tr key={script.file_id}>
                                                    <td className="h6 text-center">
                                                        <button
                                                            className="icon-only-button"
                                                            style={{ cursor: downloadingId === script.file_id ? "not-allowed" : "pointer" }}
                                                            disabled={downloadingId === script.file_id}
                                                            onClick={() => handleDownload(script)}
                                                        >
                                                            {downloadingId === script.file_id ? (
                                                                <LoadingText text="" className="mb-0" />
                                                            ) : (
                                                                <i className="bi bi-file-earmark-arrow-down-fill" aria-label="Download" title="Download"></i>
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="h6">
                                                        <a
                                                            href={`/database/script/${script.file_id}`}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/database/script/${script.file_id}`, { state: { from: "roulette" } });
                                                            }}
                                                        >
                                                            {script["Script Title"]}
                                                        </a>
                                                    </td>
                                                    <td className="h6">{script["Writer(s)"]}</td>
                                                    <td className="h6">{script["Draft Date"]}</td>
                                                    <td className="h6">{script.additional_info_concat}</td>
                                                    <td className="h6">{script["Date Added"]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
